import { z } from "zod";
import { ratingAggregateSchema } from "./rating.ts";
import { galleryImageSchema } from "./gallery.ts";

export const seriesIdSchema = z.string().min(1);
export type SeriesId = z.infer<typeof seriesIdSchema>;

export const posterAspectRatioSchema = z.enum(["9:16", "2:3", "16:9"]);
export type PosterAspectRatio = z.infer<typeof posterAspectRatioSchema>;

export const posterVariantSchema = z.object({
  avif: z.record(z.string()).optional(),
  webp: z.record(z.string()).optional(),
});

export const seriesCastSchema = z.object({
  name: z.string(),
  role: z.string().optional(),
  photoUrl: z.string().url().optional(),
});

export const seriesSchema = z.object({
  _id: z.string(),
  slug: z.string(),
  titleTh: z.string(),
  titleEn: z.string(),
  taglineTh: z.string().optional(),
  taglineEn: z.string().optional(),
  synopsisTh: z.string(),
  synopsisEn: z.string(),
  posterUrl: z.string().url(),
  posterVariants: posterVariantSchema.optional(),
  posterAspectRatio: posterAspectRatioSchema.default("9:16"),
  // Multi-image gallery — admin uploads N images per series, each tagged with
  // a role (poster, hero, still, keyart, card, social). Authoritative source
  // for surfaces beyond the single posterUrl above; posterUrl stays for
  // backwards-compat reads and a fast required-field cue.
  gallery: z.array(galleryImageSchema).default([]),
  trailerStreamVideoId: z.string().optional(),
  categories: z.array(z.string()).default([]),
  featured: z.boolean().default(false),
  published: z.boolean().default(false),
  episodeCount: z.number().int().nonnegative().default(0),
  seasonsCount: z.number().int().nonnegative().default(1),
  totalDurationSec: z.number().nonnegative().default(0),
  ageRating: z.string().optional(),
  year: z.number().int().optional(),
  cast: z.array(seriesCastSchema).default([]),
  // Legacy 0–10 score from earlier rounds. Kept for backward-compat reads.
  // New code uses `ratings` (the Netflix-style aggregate) per §5.11.
  ratingScore: z.number().min(0).max(10).optional(),
  ratingCount: z.number().int().nonnegative().optional(),
  ratings: ratingAggregateSchema.optional(),
  freeEpisodeCount: z.number().int().nonnegative().optional(),
  bundlePriceThb: z.number().nonnegative().nullable().optional(),
  deviceCapBundle: z.number().int().positive().optional(),
  deviceCapEpisode: z.number().int().positive().optional(),
  // §B1 — wizard-set datetime for a future-dated release. A background cron
  // (round-6) flips `published` to true when this passes. Null/undefined when
  // the series is either already-published or saved as draft only.
  scheduledPublishAt: z.coerce.date().nullable().optional(),
  country: z.string().optional(),
  embedding: z.array(z.number()).optional(),
  embeddingModel: z.string().optional(),
  embeddingUpdatedAt: z.coerce.date().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
});

export type Series = z.infer<typeof seriesSchema>;
